import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type JSX, useState, useEffect } from "react";
import { getSupabaseBrowserClient } from "@/lib/supabase";
import { loginWithPassword, signupWithPassword } from "@/server/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, useAuthActions } from "@/components/AuthProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Loader2 } from "lucide-react";
import {Logo} from "@/components/ui/logo";
import { z } from "zod";
import { POST_AUTH_REDIRECT } from "@/helpers/authRedirect";
import { safeRedirect } from "@/utils/safeRedirect";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://wayra.cc";

const loginSearchParams = z.object({
  redirect: z.string().optional(),
  mode: z.enum(["login", "signup"]).optional(),
});

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: loginSearchParams,
  head: () => ({
    meta: [{ name: "robots", content: "noindex, nofollow" }],
    links: [{ rel: "canonical", href: `${SITE_URL}/login` }],
  }),
});

/**
 * Login and signup page with a split-view layout on desktop.
 * Left panel has a gradient with cycling-related messaging,
 * right panel contains the auth form.
 */
function LoginPage() {
  const { redirect, mode: initialMode } = Route.useSearch();
  const [mode, setMode] = useState<"login" | "signup">(initialMode ?? "signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { invalidate } = useAuthActions();
  const {featureFlags} = Route.useRouteContext();
  const oauthProviders = featureFlags.oauth_providers;
  const { track } = useAnalytics();

  // Single source of truth for post-auth redirect: this effect fires whenever
  // `profile` transitions to a defined id — which happens after either form
  // submission's `invalidate()` or an external auth state change (e.g. user
  // landing on /login while already authenticated). handleEmailAuth used to
  // also navigate after invalidate(), which raced this effect into firing
  // twice (two fetchSessionProfile RPCs and two /search SSR navigations).
  //
  // Depend on profile?.id (value-stable), not the profile object — Profile.parse()
  // returns a fresh reference on each fetch, which would self-trigger the effect.
  useEffect(() => {
    if (profile) {
      const dest = safeRedirect(redirect, POST_AUTH_REDIRECT);
      if (dest.includes('?')) {
        window.location.assign(dest);
      } else {
        navigate({ to: dest });
      }
    }
  }, [profile?.id, navigate, redirect]);

  if (profile) {
    return null;
  }

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);
    track("auth_submit_attempted", {
      mode,
      has_email: email !== "",
      has_password: password !== "",
    });

    try {
      if (mode === "signup") {
        // Supabase embeds emailRedirectTo as the raw `next=...` value in the
        // confirmation email link without URL-encoding. An unencoded `&` in
        // the query string splits subsequent params off `next` entirely, so
        // percent-encode the query portion to keep it atomic through the
        // round-trip. URLSearchParams decodes it once on the callback side.
        const dest = safeRedirect(redirect, POST_AUTH_REDIRECT);
        const [path, query] = dest.split("?");
        const encodedDest = query
          ? `${path}?${encodeURIComponent(query)}`
          : path;
        const result = await signupWithPassword({
          data: {
            email,
            password,
            emailRedirectTo: `${window.location.origin}${encodedDest}`,
          },
        });
        if (result.error) {
          track("auth_submit_failed", {
            mode,
            error_message: result.error,
            error_source: "api",
          });
          setError(result.error);
          return;
        }
        track("auth_submit_succeeded", {
          mode,
          needs_confirmation: result.needsConfirmation,
        });
        if (result.needsConfirmation) {
          setSuccess(
            "Check your email for a confirmation link to complete signup.",
          );
          return;
        }
        // See comment in the login branch — let the redirect effect handle navigation.
        await invalidate();
      } else {
        const result = await loginWithPassword({
          data: { email, password },
        });
        if (result.error) {
          track("auth_submit_failed", {
            mode,
            error_message: result.error,
            error_source: "api",
          });
          setError(result.error);
          return;
        }
        track("auth_submit_succeeded", {
          mode,
          needs_confirmation: false,
        });
        // invalidate() refetches root context so `profile` becomes truthy;
        // the redirect effect above takes it from there. Don't navigate here —
        // doing so races the effect into running twice.
        await invalidate();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Authentication failed";
      track("auth_submit_failed", {
        mode,
        error_message: errorMessage,
        error_source: "exception",
      });
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Initiates OAuth sign-in via the browser client.
   * The browser redirects to the provider and returns to /auth/callback.
   */
  const handleOAuth = async (provider: "google" | "apple") => {
    track("auth_oauth_clicked", { provider });
    setError(null);
    const next = safeRedirect(redirect, POST_AUTH_REDIRECT);
    const callbackUrl = new URL('/auth/callback', window.location.origin);
    callbackUrl.searchParams.set('next', next);
    const supabase = getSupabaseBrowserClient();
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: callbackUrl.toString(),
      },
    });
    if (error) {
      setError(error.message);
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — gradient with messaging (desktop only) */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, #1a1a1a 0%, #2d1f14 50%, #3d2112 100%)",
        }}
      >
        <HeroPanel mode={mode} />
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen bg-white">
        {/* Top bar with logo */}
        <div className="flex items-center justify-between px-6 sm:px-10 py-6">
          <Link to="/">
            <Logo />
          </Link>
          <button
            type="button"
            onClick={toggleMode}
            className="text-sm text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
          >
            {mode === "login" ? "Create account" : "Sign in"}
          </button>
        </div>

        {/* Centered form */}
        <div className="flex-1 flex items-center justify-center px-6 sm:px-10 pb-12">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h1 className="text-2xl font-bold font-sans text-slate-900 mb-2">
                {mode === "login" ? "Welcome Back" : "Create Your Account"}
              </h1>
              <p className="text-sm text-slate-500">
                {mode === "login"
                  ? "Sign in to pick up where you left off."
                  : "Join Wayra to discover and track cycling events."}
              </p>
            </div>

            {oauthProviders.length > 0 && (
              <>
                {/* OAuth Buttons */}
                <div className="flex flex-col gap-3 mb-6">
                  {oauthProviders.map((provider) => (
                    <OAuthButton
                      key={provider}
                      provider={provider}
                      onClick={() =>
                        handleOAuth(provider as "google" | "apple")
                      }
                    />
                  ))}
                </div>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-slate-400">
                      or continue with email
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Email/Password Form */}
            <form onSubmit={handleEmailAuth} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value !== "") {
                      track("auth_email_entered", { mode });
                    }
                  }}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={
                    mode === "signup"
                      ? "At least 6 characters"
                      : "Your password"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              {success && (
                <p className="text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
                  {success}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>

            {/* Toggle Mode */}
            <p className="text-center text-sm text-slate-500 mt-8">
              {mode === "login"
                ? "Don't have an account? "
                : "Already have an account? "}
              <button
                type="button"
                onClick={toggleMode}
                className="text-primary font-medium hover:underline cursor-pointer"
              >
                {mode === "login" ? "Sign up" : "Sign in"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface HeroPanelProps {
  mode: "login" | "signup";
}

/**
 * Left-side gradient panel with cycling-related messaging.
 * Content changes based on login vs signup mode.
 */
function HeroPanel({ mode }: HeroPanelProps) {
  return (
    <div className="relative flex flex-col justify-end p-12 w-full">
      {/* Decorative gradient orbs */}
      <div
        className="absolute top-1/4 -right-20 w-80 h-80 rounded-full opacity-15 blur-3xl"
        style={{
          background:
            "linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)",
        }}
      />
      <div
        className="absolute bottom-1/3 -left-10 w-60 h-60 rounded-full opacity-10 blur-3xl"
        style={{
          background: "linear-gradient(135deg, #f97316 0%, #f87171 100%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
          {mode === "login" ? (
            <>
              Every Ride
              <br />
              Starts Here
            </>
          ) : (
            <>
              Find Your
              <br />
              Next Adventure
            </>
          )}
        </h2>
        <p className="text-white/70 text-base xl:text-lg max-w-md leading-relaxed">
          {mode === "login"
            ? "Discover cycling events, explore new routes, and join a community of riders near you."
            : "Browse hundreds of rides, races, and cycling events. Save your favorites and never miss a start."}
        </p>
      </div>
    </div>
  );
}

const oauthProviderConfig: Record<
  string,
  { label: string; icon: () => JSX.Element }
> = {
  google: { label: "Google", icon: GoogleIcon },
  apple: { label: "Apple", icon: AppleIcon },
};

interface OAuthButtonProps {
  provider: string;
  onClick: () => void;
}

/**
 * Renders an OAuth sign-in button for a given provider.
 * Falls back gracefully if the provider is not recognized.
 */
function OAuthButton({ provider, onClick }: OAuthButtonProps) {
  const config = oauthProviderConfig[provider];
  if (!config) {
    return null;
  }

  const Icon = config.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center gap-3 w-full h-11 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
    >
      <Icon />
      Continue with {config.label}
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}
